import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { DepositDisclaimer, FormNotice } from "@/components/site/Notices";
import ConsentCheckbox from "@/components/site/ConsentCheckbox";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema } from "@/components/seo/schema";

const PROJECT_TYPES = [
  "Web Development",
  "Web & Mobile App Development",
  "Custom CRM",
  "CMS Software",
  "API Integration",
  "AI Integration",
  "Hosting & Maintenance",
  "Other",
];

const DISCOUNT_OPTIONS = [
  "Military or Veteran",
  "First Responder",
  "Teacher",
  "Healthcare Worker",
  "Not applicable",
];

const BUDGET_RANGES = [
  "Under $1,000",
  "$1,000 - $2,500",
  "$2,500 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const TIMELINES = [
  "ASAP / 1-2 weeks",
  "1 month",
  "2-3 months",
  "3-6 months",
  "Flexible",
];

const EMPTY = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  current_website: "",
  project_type: "",
  budget_range: "",
  timeline: "",
  features: "",
  message: "",
  discount_eligibility: "",
  referral_code: "",
};

export default function Quote() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const prefillPackage = params.get("package");

  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (prefillPackage) {
      setForm((f) => ({
        ...f,
        message: f.message ? f.message : `I'd like a custom quote based on the "${prefillPackage}" package.`,
      }));
    }
  }, [prefillPackage]);

  const setField = (k) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    try {
      setUploading(true);
      const uploaded = [];
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      }
      setFiles((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (path) => setFiles((prev) => prev.filter((f) => f.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(EMPTY)) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete every field. Use N/A or Unknown where it does not apply.");
        return;
      }
    }
    if (!consent) {
      toast.error("Please review and agree to the Terms & Conditions to continue.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/quotes", { ...form, file_paths: files.map((f) => f.path), consent_agreement: true });
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-quote">
      <Seo
        title="Get a Free Custom Quote | Equinox Systems"
        description="Request a free custom quote for your website, web app, CRM, CMS, API, or AI integration. Veteran-owned, nationwide remote web development."
        path="/quote"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Quote", path: "/quote" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <SectionHeader
            as="h1"
            eyebrow="Custom Quote"
            title="Tell us about your project"
            description="The more detail you share, the more accurate your custom proposal will be."
          />

          <div className="space-y-4 mb-8">
            <DepositDisclaimer />
            <FormNotice />
          </div>

          <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-6" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *"><Input data-testid="quote-name" value={form.name} onChange={setField("name")} className="bg-white/5 border-white/10 text-white" placeholder="Your full name" /></Field>
              <Field label="Business Name *"><Input data-testid="quote-business" value={form.business_name} onChange={setField("business_name")} className="bg-white/5 border-white/10 text-white" placeholder="Company name or N/A" /></Field>
              <Field label="Email *"><Input data-testid="quote-email" type="email" value={form.email} onChange={setField("email")} className="bg-white/5 border-white/10 text-white" placeholder="you@business.com" /></Field>
              <Field label="Phone *"><Input data-testid="quote-phone" value={form.phone} onChange={setField("phone")} className="bg-white/5 border-white/10 text-white" placeholder="(555) 000-1234" /></Field>
              <Field label="Current Website URL *"><Input data-testid="quote-website" value={form.current_website} onChange={setField("current_website")} className="bg-white/5 border-white/10 text-white" placeholder="https:// or N/A" /></Field>
              <Field label="Project Type *">
                <Select value={form.project_type} onValueChange={setField("project_type")}>
                  <SelectTrigger data-testid="quote-project-type" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select project type" /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Budget Range *">
                <Select value={form.budget_range} onValueChange={setField("budget_range")}>
                  <SelectTrigger data-testid="quote-budget" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select budget" /></SelectTrigger>
                  <SelectContent>{BUDGET_RANGES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Desired Timeline *">
                <Select value={form.timeline} onValueChange={setField("timeline")}>
                  <SelectTrigger data-testid="quote-timeline" className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select timeline" /></SelectTrigger>
                  <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Features Needed *">
              <Textarea data-testid="quote-features" rows={3} value={form.features} onChange={setField("features")} className="bg-white/5 border-white/10 text-white" placeholder="e.g. blog, e-commerce, user auth, integrations... or N/A" />
            </Field>
            <Field label="Project Details / Message *">
              <Textarea data-testid="quote-message" rows={5} value={form.message} onChange={setField("message")} className="bg-white/5 border-white/10 text-white" placeholder="Tell us about your goals, audience, and anything else..." />
            </Field>

            <div>
              <Label className="text-gray-300 text-sm">Discount Eligibility *</Label>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                {DISCOUNT_OPTIONS.map((opt) => {
                  const checked = form.discount_eligibility === opt;
                  return (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 border cursor-pointer transition-all text-sm ${
                        checked ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/20"
                      }`}
                      data-testid={`quote-discount-${opt.replace(/[/\s]+/g, "-").toLowerCase()}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => setField("discount_eligibility")(opt)} />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <Field label="Referral Code / Name *">
              <Input data-testid="quote-referral" value={form.referral_code} onChange={setField("referral_code")} className="bg-white/5 border-white/10 text-white" placeholder="Who referred you? or N/A" />
            </Field>

            <div>
              <Label className="text-gray-300 text-sm">Attach Files (optional)</Label>
              <label htmlFor="quote-file-input" className="mt-3 flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center cursor-pointer hover:border-blue-500/40 transition-colors" data-testid="quote-file-dropzone">
                <Upload className="w-6 h-6 text-blue-400" />
                <div className="text-sm text-gray-300">{uploading ? "Uploading..." : "Click to upload files"}</div>
                <div className="text-xs text-gray-500">PDF, images, briefs · Max 15MB each</div>
                <input id="quote-file-input" type="file" multiple onChange={handleUpload} className="hidden" data-testid="quote-file-input" />
              </label>
              {files.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {files.map((f) => (
                    <li key={f.path} className="flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm">
                      <span className="truncate">{f.filename}</span>
                      <button type="button" onClick={() => removeFile(f.path)} className="text-gray-400 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <ConsentCheckbox checked={consent} onChange={setConsent} testid="quote-consent" />

            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="quote-submit-btn">
              {submitting ? "Submitting..." : "Request My Quote"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-sm">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
